// === GENERATED:SCREEN:PRODUCT-SELECTION ===
// Generated by Sluitstraat Generator
// Custom card-based product selection UI
// Step: Product Selection (Universal)

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useProductSelection,
  useStepData,
  useNavigateWithProgress,
  WorkflowPageLayout,
  calculateTotalSteps,
} from '@sluitstraat/core';
import { Check, Info } from 'phosphor-react';
import { commonStyles } from '../../styles/commonStyles';

const SCREEN_ID = 'product-selection';

/** Available product IDs */
type ProductId = 'aansprakelijkheid' | 'inboedel' | 'woonhuis';

/** Product selection state - maps product ID to selected status */
type ProductSelection = Record<ProductId, boolean>;

interface InfoItem {
  labelKey: string;
  tooltipKey: string;
}

interface Product {
  id: ProductId;
  nameKey: string;
  descriptionKey: string;
  iconSrc: string;
  iconAlt: string;
  featureKeys: string[];
  period: string;
  recommended: boolean;
  infoTitleKey: string;
  infoItems: InfoItem[];
}

const PRODUCTS: Product[] = [
  {
    id: 'aansprakelijkheid',
    nameKey: 'products.aansprakelijkheid.name',
    descriptionKey: 'products.aansprakelijkheidDescription',
    iconSrc: '/Liability_Insurance.svg',
    iconAlt: 'Liability Insurance',
    period: '/maand',
    featureKeys: [
      'products.aansprakelijkheid.infoContent.maxCoverage',
      'products.aansprakelijkheid.infoContent.householdFamily',
      'products.aansprakelijkheid.infoContent.childrenAndPetsDamage',
    ],
    recommended: false,
    infoTitleKey: 'products.aansprakelijkheid.infoTitle',
    infoItems: [
      { labelKey: 'products.aansprakelijkheid.infoContent.maxCoverage', tooltipKey: 'products.aansprakelijkheid.infoContent.maxCoverageTooltip' },
      { labelKey: 'products.aansprakelijkheid.infoContent.householdFamily', tooltipKey: 'products.aansprakelijkheid.infoContent.householdFamilyTooltip' },
      { labelKey: 'products.aansprakelijkheid.infoContent.childrenDamage', tooltipKey: 'products.aansprakelijkheid.infoContent.childrenDamageTooltip' },
      { labelKey: 'products.aansprakelijkheid.infoContent.petsDamage', tooltipKey: 'products.aansprakelijkheid.infoContent.petsDamageTooltip' },
      { labelKey: 'products.aansprakelijkheid.infoContent.helpingOthers', tooltipKey: 'products.aansprakelijkheid.infoContent.helpingOthersTooltip' },
      { labelKey: 'products.aansprakelijkheid.infoContent.homeDamage', tooltipKey: 'products.aansprakelijkheid.infoContent.homeDamageTooltip' },
    ]
  },
  {
    id: 'inboedel',
    nameKey: 'products.inboedel.name',
    descriptionKey: 'products.inboedelDescription',
    iconSrc: '/Contents_Insurance.svg',
    iconAlt: 'Contents Insurance',
    period: '/maand',
    featureKeys: [
      'products.inboedel.infoContent.damageContents',
      'products.inboedel.infoContent.replacementValue',
      'products.inboedel.infoContent.tenantsOwners',
    ],
    recommended: false,
    infoTitleKey: 'products.inboedel.infoTitle',
    infoItems: [
      { labelKey: 'products.inboedel.infoContent.damageContents', tooltipKey: 'products.inboedel.infoContent.damageContentsTooltip' },
      { labelKey: 'products.inboedel.infoContent.replacementValue', tooltipKey: 'products.inboedel.infoContent.replacementValueTooltip' },
      { labelKey: 'products.inboedel.infoContent.tenantsOwners', tooltipKey: 'products.inboedel.infoContent.tenantsOwnersTooltip' },
      { labelKey: 'products.inboedel.infoContent.jewellery', tooltipKey: 'products.inboedel.infoContent.jewelleryTooltip' },
      { labelKey: 'products.inboedel.infoContent.outsideHome', tooltipKey: 'products.inboedel.infoContent.outsideHomeTooltip' },
      { labelKey: 'products.inboedel.infoContent.allRisk', tooltipKey: 'products.inboedel.infoContent.allRiskTooltip' },
      { labelKey: 'products.inboedel.infoContent.optionalCyber', tooltipKey: 'products.inboedel.infoContent.optionalCyberTooltip' },
    ]
  },
  {
    id: 'woonhuis',
    nameKey: 'products.woonhuis.name',
    descriptionKey: 'products.woonhuisDescription',
    iconSrc: '/Home_Insurance.svg',
    iconAlt: 'Home Insurance',
    period: '/maand',
    featureKeys: [
      'products.woonhuis.infoContent.damageHome',
      'products.woonhuis.infoContent.coverageChoice',
      'products.woonhuis.infoContent.glassIncluded',
    ],
    recommended: false,
    infoTitleKey: 'products.woonhuis.infoTitle',
    infoItems: [
      { labelKey: 'products.woonhuis.infoContent.damageHome', tooltipKey: 'products.woonhuis.infoContent.damageHomeTooltip' },
      { labelKey: 'products.woonhuis.infoContent.coverageChoice', tooltipKey: 'products.woonhuis.infoContent.coverageChoiceTooltip' },
      { labelKey: 'products.woonhuis.infoContent.reconstructionValue', tooltipKey: 'products.woonhuis.infoContent.reconstructionValueTooltip' },
      { labelKey: 'products.woonhuis.infoContent.glassIncluded', tooltipKey: 'products.woonhuis.infoContent.glassIncludedTooltip' },
      { labelKey: 'products.woonhuis.infoContent.gardenPlants', tooltipKey: 'products.woonhuis.infoContent.gardenPlantsTooltip' },
      { labelKey: 'products.woonhuis.infoContent.sustainableEnergy', tooltipKey: 'products.woonhuis.infoContent.sustainableEnergyTooltip' },
      { labelKey: 'products.woonhuis.infoContent.optionalGlass', tooltipKey: 'products.woonhuis.infoContent.optionalGlassTooltip' },
    ]
  },
];

export const ProductSelectionScreen: React.FC = () => {
  const navigate = useNavigateWithProgress();
  const { t } = useTranslation();
  const { selectedProducts, setSelectedProducts } = useProductSelection();
  const { getStepData, saveStepData } = useStepData();

  // Initialize from context or localStorage
  const [selection, setSelection] = useState<ProductSelection>(() => {
    if (selectedProducts && Object.keys(selectedProducts).length > 0) {
      return selectedProducts as ProductSelection;
    }
    const saved = getStepData(SCREEN_ID);
    return saved?.selectedProducts || { aansprakelijkheid: false, inboedel: false, woonhuis: false };
  });

  const [showValidationError, setShowValidationError] = useState<boolean>(false);
  const [hoveredCard, setHoveredCard] = useState<ProductId | null>(null);
  const [activeInfo, setActiveInfo] = useState<ProductId | null>(null);

  // Clear stored IDs when starting a fresh workflow (but not when navigating back within the workflow)
  useEffect(() => {
    // Check if user completed the previous workflow (reached end page)
    const workflowCompleted = sessionStorage.getItem('workflow_completed') === 'true';

    // Check if user is navigating BACK within the workflow vs starting fresh
    const lastWorkflowPage = sessionStorage.getItem('workflow_current_page');
    const workflowPageOrder = [
      'product-selection',
      'client-information',
      'aansprakelijkheid-cvb',
      'inboedel-cvb',
      'woonhuis-cvb',
      'summary',
      'payment-information',
      'acceptance-questions',
      'confirmation',
      'end'
    ];

    const currentPageIndex = workflowPageOrder.indexOf('product-selection');
    const lastPageIndex = lastWorkflowPage ? workflowPageOrder.indexOf(lastWorkflowPage) : -1;

    // User is navigating back within the workflow if:
    // - lastWorkflowPage exists AND
    // - lastWorkflowPage is after product-selection in the workflow order
    // - AND the workflow was NOT completed (user didn't reach end page)
    const isNavigatingBackWithinWorkflow = lastPageIndex > currentPageIndex && !workflowCompleted;

    // Check if we have stored session IDs that could cause duplicate relations
    const storedRelationId = sessionStorage.getItem('vp_idnr');
    const storedCalculationId = sessionStorage.getItem('calculation_id');
    const storedQuoteId = sessionStorage.getItem('quote_id');
    const hasStoredIds = storedRelationId || storedCalculationId || storedQuoteId;

    // If NOT navigating back within workflow, clear the session IDs to start fresh
    // This ensures a new relation is created when the user starts the flow again
    if (hasStoredIds && !isNavigatingBackWithinWorkflow) {
      // Clear request submission markers first (need IDs before clearing)
      if (storedRelationId && storedQuoteId) {
        sessionStorage.removeItem(`request_submitted_${storedRelationId}_${storedQuoteId}`);
        sessionStorage.removeItem(`email_sent_${storedRelationId}_${storedQuoteId}`);
      }
      // Clear all workflow session IDs
      sessionStorage.removeItem('vp_idnr');
      sessionStorage.removeItem('calculation_id');
      sessionStorage.removeItem('quote_id');
      sessionStorage.removeItem('request_id');
      sessionStorage.removeItem('enriched_calculation_document');
      sessionStorage.removeItem('confetti_shown');
      sessionStorage.removeItem('inboedel_infofolio_fields');
      sessionStorage.removeItem('woonhuis_infofolio_fields');
      sessionStorage.removeItem('package_discount_applied');
      sessionStorage.removeItem('calculation_completed');
      sessionStorage.removeItem('quote_generated');
      sessionStorage.removeItem('product_data_hash');
      sessionStorage.removeItem('workflow_chain_completed_hash');
    }

    // Clear the workflow_completed flag so we don't keep resetting on every visit
    if (workflowCompleted) {
      sessionStorage.removeItem('workflow_completed');
    }

    // Update current page tracker
    sessionStorage.setItem('workflow_current_page', 'product-selection');
    sessionStorage.setItem('workflow_session_active', 'true');
  }, []);

  const toggleProduct = useCallback((productId: ProductId) => {
    setSelection((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
    setShowValidationError(false);
  }, []);

  const handleNext = useCallback(() => {
    // Validate: at least one product must be selected
    const hasSelection = Object.values(selection).some((val) => val === true);

    if (!hasSelection) {
      setShowValidationError(true);
      return;
    }

    // Save data and update selected products in context
    saveStepData(SCREEN_ID, { selectedProducts: selection });
    if (setSelectedProducts) {
      setSelectedProducts(selection);
    }

    // Navigate to next screen (client-information)
    navigate('/workflow/client-information');
  }, [selection, saveStepData, setSelectedProducts, navigate]);

  const selectedCount = Object.values(selection).filter((v) => v).length;

  return (
    <WorkflowPageLayout currentStep={1} totalSteps={calculateTotalSteps(selection)}>
      <main style={styles.page}>
        <div style={styles.container}>
          {/* Header */}
          <header style={styles.contentHeader}>
            <h1 style={styles.title}>{t('products.selectCoverage')}</h1>
            <p style={styles.subtitle}>
              {t('products.selectSubtitle')}
            </p>
            <p style={styles.mobileHint} className="mobile-hint">
              {t('products.tapToSelect')}
            </p>
          </header>

          {showValidationError && (
            <div style={styles.validationError}>
              {t('products.selectAtLeastOne')}
            </div>
          )}

          {/* Product Cards - 3 Column Grid */}
          <div className="products-grid-responsive" style={styles.productsGrid}>
            {PRODUCTS.map((product) => {
              const isSelected = selection[product.id] === true;
              const isHovered = hoveredCard === product.id;

              return (
                <div
                  key={product.id}
                  style={styles.cardWrapper}
                >
                  {/* Recommended Badge */}
                  {product.recommended && (
                    <div style={styles.recommendedBadge}>
                      <span style={styles.recommendedBadgeContent}>
                        {t('products.mostChosen')}
                      </span>
                    </div>
                  )}

                  <div
                    style={{
                      ...styles.productCard,
                      ...(isSelected ? styles.productCardSelected : {}),
                      ...(isHovered && !isSelected ? styles.productCardHovered : {}),
                    }}
                    onClick={() => toggleProduct(product.id)}
                    onMouseEnter={() => setHoveredCard(product.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleProduct(product.id);
                      }
                    }}
                    aria-pressed={isSelected}
                    aria-label={`${t(product.nameKey)} - ${isSelected ? t('products.selected') : t('products.tapToSelect')}`}
                  >
                    {/* Selection Indicator - always visible */}
                    <div style={isSelected ? styles.selectedBadge : styles.unselectedBadge}>
                      {isSelected ? (
                        <Check style={{ width: 16, height: 16, color: 'white' }} />
                      ) : (
                        <div style={styles.emptyCircle} />
                      )}
                    </div>

                    <div style={styles.cardHeader}>
                      {/* Icon with duotone styling */}
                      <div style={{
                        ...styles.iconContainer,
                        ...(isSelected ? styles.iconContainerSelected : {}),
                      }}>
                        <img
                          src={product.iconSrc}
                          alt={product.iconAlt}
                          width={28}
                          height={28}
                          style={{ filter: isSelected ? 'brightness(0) invert(1)' : 'none' }}
                        />
                      </div>

                      <div style={styles.productNameRow}>
                        <h3 style={styles.productName}>{t(product.nameKey)}</h3>
                        <div
                          style={styles.infoButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveInfo(activeInfo === product.id ? null : product.id);
                          }}
                        >
                          <Info size={18} color="#1976d2" />
                        </div>
                      </div>

                      <p style={styles.productDescription}>{t(product.descriptionKey)}</p>
                    </div>

                    <div style={styles.cardContent}>
                      {/* Features List */}
                      <ul style={styles.productFeatures}>
                        {product.featureKeys.map((featureKey, idx) => (
                          <li key={idx} style={styles.featureItem}>
                            <Check style={{
                              ...styles.featureCheck,
                              color: isSelected ? 'var(--primary-color, #1976d2)' : '#999',
                            }} />
                            <span style={styles.featureText}>{t(featureKey)}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        style={styles.seeMoreButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveInfo(activeInfo === product.id ? null : product.id);
                        }}
                      >
                        {t('common.seeMore')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Section with Continue Button */}
          <div style={styles.bottomSection} className="bottom-section-responsive">
            <div style={styles.selectionSummary} className="selection-summary-responsive">
              {selectedCount === 0 ? (
                <span>{t('products.noProductsSelected')}</span>
              ) : (
                <span>
                  {t('products.productsSelected', { count: selectedCount })}
                </span>
              )}
            </div>

            <nav style={styles.navigationSingle} className="navigation-responsive">
              <button
                onClick={handleNext}
                disabled={selectedCount === 0}
                style={{
                  ...styles.buttonPrimary,
                  ...(selectedCount === 0 ? styles.buttonDisabled : {}),
                }}
              >
                {t('common.next')}
              </button>
            </nav>
          </div>
        </div>

        {/* Info Modal Overlay */}
        {activeInfo && (() => {
          const activeProduct = PRODUCTS.find(p => p.id === activeInfo);
          if (!activeProduct) return null;
          return (
            <div style={styles.modalOverlay} onClick={() => setActiveInfo(null)}>
              <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div style={styles.infoPopupHeader}>
                  <h4 style={styles.infoPopupTitle}>{t(activeProduct.infoTitleKey)}</h4>
                  <button
                    style={styles.modalCloseButton}
                    onClick={() => setActiveInfo(null)}
                    aria-label="Close"
                  >
                    &times;
                  </button>
                </div>
                <ul style={styles.infoList}>
                  {activeProduct.infoItems.map((item, idx) => (
                    <li key={idx} style={styles.infoListItem}>
                      <Check size={16} color="#22c55e" style={{ flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <span style={styles.infoItemLabel}>{t(item.labelKey)}</span>
                        <p style={styles.infoItemTooltip}>{t(item.tooltipKey)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })()}
      </main>
    </WorkflowPageLayout>
  );
};

const styles = {
  ...commonStyles,
  // Page-specific overrides
  page: {
    ...commonStyles.page,
    padding: '8px 16px 40px',
  },
  container: {
    ...commonStyles.container,
    margin: '4em auto',
  },
  contentHeader: commonStyles.contentHeader,
  title: commonStyles.title,
  subtitle: {
    ...commonStyles.subtitle,
    fontSize: '18px',
    maxWidth: '672px',
    lineHeight: '1.5',
  },
  mobileHint: {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '8px',
    display: 'none',
    fontStyle: 'italic' as const,
  },
  validationError: commonStyles.validationError,
  productsGrid: commonStyles.productsGrid,
  cardWrapper: {
    ...commonStyles.cardWrapper,
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
  },
  recommendedBadge: commonStyles.recommendedBadge,
  recommendedBadgeContent: commonStyles.recommendedBadgeContent,
  productCard: commonStyles.productCard,
  productCardHovered: commonStyles.productCardHovered,
  productCardSelected: commonStyles.productCardSelected,
  selectedBadge: commonStyles.selectedBadge,
  unselectedBadge: {
    position: 'absolute' as const,
    top: '16px',
    right: '16px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid #d1d5db',
    backgroundColor: 'var(--background-color, #f9f6ee)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  emptyCircle: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
  },
  cardHeader: commonStyles.cardHeader,
  iconContainer: commonStyles.iconContainer,
  iconContainerSelected: commonStyles.iconContainerSelected,
  productName: {
    ...commonStyles.productName,
    marginBottom: '0',
    lineHeight: '1.2',
  },
  productDescription: commonStyles.productDescription,
  cardContent: commonStyles.cardContent,
  productFeatures: commonStyles.productFeatures,
  featureItem: commonStyles.featureItem,
  featureCheck: commonStyles.featureCheck,
  featureText: commonStyles.featureText,
  bottomSection: commonStyles.bottomSection,
  selectionSummary: commonStyles.selectionSummary,
  navigationSingle: commonStyles.navigationSingle,
  buttonPrimary: {
    ...commonStyles.buttonPrimary,
    padding: '16px 48px',
    minWidth: '200px',
    width: 'auto',
    justifyContent: 'center',
  },
  buttonDisabled: commonStyles.buttonDisabled,
  productNameRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '4px',
    marginBottom: '8px',
  },
  infoButton: {
    cursor: 'help',
    padding: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'opacity 0.2s',
    opacity: 0.8,
    marginTop: '-2px',
  },
  seeMoreButton: {
    alignSelf: 'flex-start' as const,
    marginTop: '12px',
    background: 'transparent',
    border: 'none',
    padding: '4px 0',
    color: 'var(--primary-color, #1976d2)',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '520px',
    width: '90%',
    maxHeight: '80vh',
    overflowY: 'auto' as const,
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '0 4px',
    lineHeight: 1,
  },
  infoPopupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  infoPopupTitle: {
    fontSize: '16px',
    fontWeight: '600' as const,
    color: '#1f2937',
    margin: 0,
  },
  infoList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  infoListItem: {
    display: 'flex',
    gap: '8px',
    fontSize: '14px',
    color: '#374151',
    lineHeight: '1.5',
  },
  infoItemLabel: {
    fontWeight: '600' as const,
    display: 'block',
  },
  infoItemTooltip: {
    margin: '2px 0 0 0',
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.4',
  },
};

export default ProductSelectionScreen;
